import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import { LegendItem } from './LegendItem';
import { convertCurrency } from 'enrollment/utils/currency/utils';

const EE_CONTRIBUTION_COLOR = '#07B200';
const EE_LUMP_SUM_COLOR = '#266923';
const ER_LUMP_SUM_COLOR = '#AE71FF';

interface ContributionGraphProps {
  eeContribution: number;
  eeLumpSumAmount?: number;
  erLumpSumAmount: number;
  irsMax: number;
}

export const ContributionGraph = ({
  eeContribution,
  eeLumpSumAmount,
  erLumpSumAmount,
  irsMax
}: ContributionGraphProps) => {
  const { t } = useTranslation('pages.financial');
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [graphWidth, setGraphWidth] = useState(0);

  useEffect(() => {
    const updateGraphWidth = () => {
      if (graphContainerRef.current) {
        setGraphWidth(graphContainerRef.current.offsetWidth);
      }
    };

    // Set initial width
    updateGraphWidth();

    // Update width on window resize
    window.addEventListener('resize', updateGraphWidth);
    return () => {
      window.removeEventListener('resize', updateGraphWidth);
    };
  }, []);

  const eeContributionPercentage = Math.round((eeContribution / irsMax) * 100) * 100 / 100;
  const eeLumpSumPercentage = Math.round(((eeLumpSumAmount || 0) / irsMax) * 100) * 100 / 100;
  const erLumpSumPercentage = Math.round(((erLumpSumAmount || 0) / irsMax) * 100) * 100 / 100;

  const displayERContribution = erLumpSumPercentage > 0;

  return (
    <>
      <div className="row mt-2">
        <div className="col d-flex align-items-center">
          <div style={{ height: '26px', width: '1px', background: 'var(--neutral-grey-500, #55575E)' }} />
          <div className="d-flex align-items-center" ref={graphContainerRef} style={{ height: '16px', width: `100%`, background: 'var(--neutral-grey-200, #E6E6E6)' }}>
            <div style={{ height: '100%', width: `${(graphWidth / 100) * eeContributionPercentage}px`, background: EE_CONTRIBUTION_COLOR}} />
            <div style={{ height: '100%', width: `${(graphWidth / 100) * eeLumpSumPercentage}px`, background: EE_LUMP_SUM_COLOR}} />
            <div style={{ height: '100%', width: `${(graphWidth / 100) * erLumpSumPercentage}px`, background: ER_LUMP_SUM_COLOR}} />
          </div>
          <div style={{ height: '26px', width: '1px', background: 'var(--neutral-grey-500, #55575E)' }} />
          <p className="mb-0 ml-2">{convertCurrency(String(irsMax))}<br />{t('irsMax')}</p>
        </div>
      </div>
      <div className="row mt-2 mb-5">
        <LegendItem label={t('lumpSum.yourContribution')} color={EE_CONTRIBUTION_COLOR} />
        <LegendItem label={t('lumpSum.lumpSumContribution')} color={EE_LUMP_SUM_COLOR} />
        {displayERContribution &&
          <LegendItem label={t('lumpSum.employerContribution')} color={ER_LUMP_SUM_COLOR} />
        }
      </div>
    </>
  );
};